/**
 * Omission stamps (SPEC §4, Components block 12). Recorded omissions
 * (`qaFindings.sectionOmissions`) hide their chapters; this lists them in one
 * collapsed block so the reader sees what is missing and why, instead of a
 * silent gap. Chapters kept with limits (contacts, next actions) get the
 * "Retained with limits" notice above their prose instead.
 *
 * Chapter numbers are deliberately not printed: hidden chapters are not
 * numbered in the table of contents, so a number here would disagree with it.
 */
import React from 'react';
import type { ReportTheme } from '../../../../../types/reportTheme';
import { roleColor } from '../../../../../lib/reportRoleColors';
import { REPORT_TEXT_PX } from '../../../../../lib/reportSectionHeading';
import { getSectionLabel } from '../../../../../config/reportSections';
import { PSR_RETAINED_LABELS, type PsrOmissionGroup } from '../../../../../lib/sentiment/psrReportView';
import { PSR_APPARATUS, PSR_SERIF, PsrCalloutLabel, PsrCaveat } from './psrUi';

const STAMP: Record<PsrOmissionGroup['reason'], string> = {
  'insufficient-evidence': 'Omitted · insufficient evidence',
  'source-unavailable': 'Omitted · source unavailable',
};

export function SentimentOmissionList({ theme, groups }: { theme: ReportTheme; groups: PsrOmissionGroup[] }): React.ReactElement | null {
  if (!groups.length) return null;
  const accent = roleColor(theme, 'accent');
  return (
    <div data-testid="psr-omission-list" data-span-columns style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {groups.map(group => (
        <div key={group.reason} data-omission-reason={group.reason} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div data-layout-group="row-group" style={{ borderTop: `1px solid ${roleColor(theme, 'rule')}` }}>
            {group.rows.map(row => (
              <div
                key={row.sectionId}
                data-layout-group="row"
                data-omitted-section={row.sectionId}
                style={{ display: 'flex', gap: 16, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', padding: '12px 0', borderBottom: `1px solid ${roleColor(theme, 'hair')}` }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: '1 1 18rem', minWidth: 0 }}>
                  <span style={{ fontFamily: 'var(--heading-font, var(--report-body-font, inherit))', fontSize: 16, fontWeight: 600, color: roleColor(theme, 'ink') }}>
                    {getSectionLabel(row.sectionId, 'public-sentiment')}
                  </span>
                  <span style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.annotation, color: roleColor(theme, 'faint') }}>{row.description}</span>
                </div>
                <span data-omission-stamp style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.annotation, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: accent, whiteSpace: 'nowrap', flex: 'none' }}>
                  <PsrCalloutLabel icon="omit">{STAMP[group.reason]}</PsrCalloutLabel>
                </span>
              </div>
            ))}
          </div>
          <PsrCaveat theme={theme} icon="omit" label={`Why ${group.rows.length === 1 ? 'this chapter is' : 'these chapters are'} omitted`} testId={`psr-omission-detail-${group.reason}`}>
            {group.detail ? group.detail : (
              <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {group.details.map(row => (
                  <span key={row.sectionId}><span style={{ fontWeight: 600 }}>{getSectionLabel(row.sectionId, 'public-sentiment')}.</span> {row.detail}</span>
                ))}
              </span>
            )}
          </PsrCaveat>
        </div>
      ))}
    </div>
  );
}

/** Above a chapter kept with limits: its omission detail, verbatim. */
export function RetainedWithLimitsNotice({ theme, sectionId, detail }: { theme: ReportTheme; sectionId: 'stakeholderMap' | 'mitigationPlaybook'; detail: string }): React.ReactElement {
  return (
    <PsrCaveat theme={theme} label={PSR_RETAINED_LABELS[sectionId]} testId={`psr-retained-${sectionId}`}>
      <span style={{ fontFamily: PSR_SERIF }}>{detail}</span>
    </PsrCaveat>
  );
}
